import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import * as s from '../styles/gitStyles';
import type { GitFileRef } from '../types';

export interface GitFileContentViewerProps {
  selectedFile: GitFileRef | null;
  selectedFileContent: string | null;
  loadingFileContent: boolean;
  selectedBranch: string;
  getLanguageForFile: (fileName: string) => string;
  getSelectedFileContent: () => { fileName: string; language: string; code: string } | null;
  /** Если true — шапка с именем файла рисуется снаружи, здесь только сам код. */
  hideHeader?: boolean;
}

export function GitFileContentViewer({
  selectedFile,
  loadingFileContent,
  selectedBranch,
  getLanguageForFile,
  getSelectedFileContent,
  hideHeader,
}: GitFileContentViewerProps) {
  if (!selectedFile) {
    return (
      <Box sx={s.emptyState}>
        <Typography variant="h6" color="text.secondary">
          Выберите файл для просмотра
        </Typography>
      </Box>
    );
  }

  const fileContent = getSelectedFileContent();

  return (
    <>
      {!hideHeader && (
        <Box sx={s.fileHeader}>
          <Box sx={s.fileHeaderTitle}>
            <Box>
              <Typography variant="h6">{selectedFile.name}</Typography>
              {fileContent && (
                <Typography variant="caption" color="text.secondary">
                  {getLanguageForFile(selectedFile.name).toUpperCase()} •{' '}
                  {fileContent.code.split('\n').length} строк
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                {selectedBranch}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={s.fileContentBox}>
        {loadingFileContent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          fileContent && (
            <SyntaxHighlighter
              language={getLanguageForFile(selectedFile.name)}
              style={tomorrow}
              customStyle={{
                margin: 0,
                borderRadius: 4,
                fontSize: '14px',
                lineHeight: '1.5',
              }}
              showLineNumbers
              wrapLines
              useInlineStyles
            >
              {fileContent.code}
            </SyntaxHighlighter>
          )
        )}
      </Box>
    </>
  );
}
